import { Sentry } from '@/lib/sentry';

export interface FeedbackSentryIds {
  sentryEventId: string | null;
  sentryReplayId: string | null;
}

const FLUSH_TIMEOUT_MS = 1000;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(undefined), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      () => { clearTimeout(timer); resolve(undefined); },
    );
  });
}

/**
 * FIN-869 — consultant-app counterpart of apps/client/lib/feedback/sentryIds.ts (FIN-867/PR4).
 * EC9: best-effort capture of the current Sentry replay/event ids at feedback-submit time.
 * Sentry being absent (no VITE_SENTRY_DSN — src/lib/sentry.ts's initSentry() never calls
 * Sentry.init), an ad blocker, or no buffered error/session all resolve to null ids within ~1s —
 * this must never block or delay the submit itself, and a failure here is never surfaced to the
 * user.
 */
export async function getFeedbackSentryIds(): Promise<FeedbackSentryIds> {
  try {
    const replay = Sentry.getReplay?.();
    if (!replay) {
      return { sentryEventId: Sentry.lastEventId() ?? null, sentryReplayId: null };
    }

    await withTimeout(replay.flush(), FLUSH_TIMEOUT_MS);
    return {
      sentryEventId: Sentry.lastEventId() ?? null,
      sentryReplayId: replay.getReplayId() ?? null,
    };
  } catch {
    return { sentryEventId: null, sentryReplayId: null };
  }
}
